import { AutoAwesome as GenerateIcon, CloudUpload as UploadIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useState } from "react";
import { BRAND_PRIMARY, BRAND_TERTIARY, STATUS_COLORS } from "@/theme/theme";
import { useApiMutation } from "../hooks/use-api-mutation";
import { useCreateFlow } from "../hooks/use-flows";
import { parseXLSX } from "../utils/import-parser";
import { clickableProps } from "../utils/keyboard";

interface SimplifiedImportWizardProps {
  open: boolean;
  onClose: () => void;
}

interface SheetConfig {
  name: string;
  selected: boolean;
  firstRow: number;
  lastRow: number;
  totalRows: number;
}

interface ImportProgress {
  total: number;
  completed: number;
  current: string;
  isGenerating: boolean;
}

// Define proper types for flow data
interface ExtractedFlowData {
  steps: {
    index: number;
    task: string;
    used_variables?: string[];
    task_variables: { var_name: string; var_type: string }[];
    reasoning: string;
    step_validation?: string;
    url: string;
  }[];
  is_positive: boolean;
  success_criteria: string;
  reasoning: string;
  confidence: string;
  severity: string;
  priority: string;
}

export const SimplifiedImportWizard: React.FC<SimplifiedImportWizardProps> = ({
  open,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<SheetConfig[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: createFlow } = useCreateFlow();
  const { mutateAsync: extractFlow } = useApiMutation<
    { content: string; persist?: boolean },
    { name: string; slug: string; description: string; data: ExtractedFlowData }
  >("POST", "/flows-extract");
  const { enqueueSnackbar } = useSnackbar();

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    setIsProcessing(true);

    try {
      const fileExtension = selectedFile.name.toLowerCase();

      if (!fileExtension.endsWith(".xlsx")) {
        setError("Please upload an Excel (.xlsx) file");
        setIsProcessing(false);
        return;
      }

      // Parse XLSX to get available sheets
      const response = await fetch(URL.createObjectURL(selectedFile));
      const arrayBuffer = await response.arrayBuffer();
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      const sheetConfigs: SheetConfig[] = workbook.SheetNames.map((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        }) as string[][];
        const totalRows = jsonData.filter((row) =>
          row?.some((cell) => cell !== undefined && cell !== ""),
        ).length;

        return {
          name: sheetName,
          selected: false,
          firstRow: 1,
          lastRow: Math.max(1, totalRows),
          totalRows,
        };
      });

      setSheets(sheetConfigs);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to process file";
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleSheetToggle = useCallback((sheetName: string, selected: boolean) => {
    setSheets((prev) =>
      prev.map((sheet) => (sheet.name === sheetName ? { ...sheet, selected } : sheet)),
    );
  }, []);

  const handleRowRangeChange = useCallback(
    (sheetName: string, field: "firstRow" | "lastRow", value: number) => {
      setSheets((prev) =>
        prev.map((sheet) => {
          if (sheet.name === sheetName) {
            const updatedSheet = { ...sheet };
            if (field === "firstRow") {
              updatedSheet.firstRow = Math.max(1, value);
              updatedSheet.lastRow = Math.max(updatedSheet.firstRow, updatedSheet.lastRow);
            } else {
              updatedSheet.lastRow = Math.max(
                updatedSheet.firstRow,
                Math.min(sheet.totalRows, value),
              );
            }
            return updatedSheet;
          }
          return sheet;
        }),
      );
    },
    [],
  );

  const processRows = useCallback(
    async (rowsData: string[][]): Promise<number> => {
      // Filter out empty rows
      const validRows = rowsData.filter((rowData) => {
        const content = rowData.map((cell) => String(cell || "")).join(" | ");
        return content.trim().length > 0;
      });

      if (validRows.length === 0) {
        return 0;
      }

      // Convert all rows to content strings
      const contents = validRows.map((rowData) =>
        rowData.map((cell) => String(cell || "")).join(" | "),
      );

      // Step 1: Extract all flows in parallel
      const extractedFlows = await Promise.all(
        contents.map(async (content) => {
          const extractedFlow = await extractFlow({ content });
          return { content, extractedFlow };
        }),
      );

      // Step 2: Create all flows in parallel
      await Promise.all(
        extractedFlows.map(({ extractedFlow }) =>
          createFlow({
            name: extractedFlow.name,
            slug: extractedFlow.slug,
            description: extractedFlow.description,
            steps: extractedFlow.data.steps,
            is_positive: extractedFlow.data.is_positive,
            success_criteria: extractedFlow.data.success_criteria,
            need_user_input: false,
            reasoning: extractedFlow.data.reasoning,
            confidence: extractedFlow.data.confidence as "low" | "medium" | "high",
            severity: extractedFlow.data.severity as "low" | "high",
            priority: extractedFlow.data.priority as "low" | "medium" | "high" | "urgent",
          }),
        ),
      );

      return extractedFlows.length;
    },
    [extractFlow, createFlow],
  );

  const handleGenerateFlows = useCallback(async () => {
    if (!file) return;

    const selectedSheets = sheets.filter((sheet) => sheet.selected);
    if (selectedSheets.length === 0) {
      setError("Please select at least one sheet");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Calculate total rows to process
      let totalRows = 0;
      for (const sheet of selectedSheets) {
        const rowsInSheet = sheet.lastRow - sheet.firstRow + 1;
        totalRows += rowsInSheet;
      }

      if (totalRows === 0) {
        setError("No rows selected to process");
        setIsProcessing(false);
        return;
      }

      setImportProgress({
        total: totalRows,
        completed: 0,
        current: "",
        isGenerating: true,
      });

      let completed = 0;

      // Process each selected sheet
      for (const sheet of selectedSheets) {
        try {
          // Parse the specific sheet
          const parsedData = await parseXLSX(file, sheet.name, sheet.firstRow - 1, 250);
          // Get rows in the specified range (1-based to 0-based conversion)
          const selectedRows = parsedData.rows.slice(0, sheet.lastRow - sheet.firstRow + 1);
          setImportProgress((prev) =>
            prev
              ? {
                  ...prev,
                  current: `Processing ${sheet.name} (${selectedRows.length} rows)`,
                }
              : null,
          );

          // Convert all rows to arrays of strings
          const rowsData = selectedRows.map((row) =>
            Object.values(row as Record<string, unknown>).map((val) => String(val || "")),
          );

          // Process all rows in parallel
          const processedCount = await processRows(rowsData);
          completed += processedCount;

          setImportProgress((prev) =>
            prev
              ? {
                  ...prev,
                  completed,
                }
              : null,
          );
        } catch {
          // Continue with next sheet instead of failing completely
        }
      }

      enqueueSnackbar(
        `Successfully generated ${completed} flows from ${selectedSheets.length} sheet(s)!`,
        { variant: "success" },
      );

      setImportProgress(null);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to generate flows";
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [file, sheets, processRows, enqueueSnackbar, onClose]);

  const handleClose = useCallback(() => {
    setFile(null);
    setSheets([]);
    setIsProcessing(false);
    setImportProgress(null);
    setError(null);
    onClose();
  }, [onClose]);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect],
  );

  const selectedSheetsCount = sheets.filter((s) => s.selected).length;
  const totalRowsToProcess = sheets
    .filter((s) => s.selected)
    .reduce((total, sheet) => total + (sheet.lastRow - sheet.firstRow + 1), 0);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: "80vh",
          maxHeight: "80vh",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack spacing={1} sx={{ height: "100%", flex: 1, overflow: "hidden" }}>
          {/* File Upload Section */}
          {!file && (
            <Paper
              sx={{
                p: 4,
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 2,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                "&:hover": {
                  borderColor: BRAND_PRIMARY,
                  backgroundColor: "grey.50",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(22, 0, 234, 0.1)",
                },
              }}
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              {...clickableProps(() => document.getElementById("file-input")?.click())}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".xlsx"
                onChange={handleFileInput}
                style={{ display: "none" }}
              />
              <UploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: "text.primary" }}>
                Upload Excel File
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Drag and drop your Excel file here, or click to browse
              </Typography>
            </Paper>
          )}

          {/* Sheet Selection Section */}
          {file && sheets.length > 0 && !importProgress && (
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Header with File Info */}
              <Paper
                sx={{
                  p: 3,
                  mb: 2,
                  background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Excel file ready for import
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={4} alignItems="center">
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND_PRIMARY }}>
                        {sheets.length}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Sheets
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: selectedSheetsCount > 0 ? "success.main" : "text.secondary",
                        }}
                      >
                        {selectedSheetsCount}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Selected
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                        {totalRowsToProcess}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Total Rows
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>

              {/* Sheet Configuration */}
              <Paper
                sx={{
                  p: 0,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Configuration Header */}
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>
                    Configure Import Settings
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Select sheets and specify row ranges for data extraction
                  </Typography>
                </Box>

                {/* Sheets List */}
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  <Stack spacing={1}>
                    {sheets.map((sheet) => (
                      <Paper
                        key={sheet.name}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          border: sheet.selected ? "2px solid" : "1px solid",
                          borderColor: sheet.selected ? "primary.main" : "divider",
                          background: sheet.selected
                            ? "linear-gradient(135deg, rgba(22, 0, 234, 0.02) 0%, rgba(183, 249, 247, 0.05) 100%)"
                            : "common.white",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: "0 4px 12px rgba(22, 0, 234, 0.08)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <Stack spacing={2}>
                          {/* Sheet Header */}
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={sheet.selected}
                                    onChange={(e) =>
                                      handleSheetToggle(sheet.name, e.target.checked)
                                    }
                                    sx={{
                                      color: BRAND_PRIMARY,
                                      "&.Mui-checked": {
                                        color: BRAND_PRIMARY,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      color: "text.primary",
                                    }}
                                  >
                                    {sheet.name}
                                  </Typography>
                                }
                              />
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: "background.default",
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 500,
                                    color: "text.secondary",
                                  }}
                                >
                                  {sheet.totalRows} total rows
                                </Typography>
                              </Box>
                            </Stack>

                            {sheet.selected && (
                              <Box
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: STATUS_COLORS.success.bg,
                                  border: `1px solid ${STATUS_COLORS.success.border}`,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: STATUS_COLORS.success.text,
                                  }}
                                >
                                  {sheet.lastRow - sheet.firstRow + 1} rows selected
                                </Typography>
                              </Box>
                            )}
                          </Stack>

                          {/* Row Range Configuration */}
                          {sheet.selected && (
                            <Box
                              sx={{
                                pl: 4,
                                pr: 2,
                                py: 2,
                                backgroundColor: "common.white",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 2,
                                  fontWeight: 500,
                                  color: "text.primary",
                                }}
                              >
                                Row Range Configuration
                              </Typography>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <TextField
                                  label="First Row"
                                  type="number"
                                  value={sheet.firstRow}
                                  onChange={(e) =>
                                    handleRowRangeChange(
                                      sheet.name,
                                      "firstRow",
                                      parseInt(e.target.value, 10) || 1,
                                    )
                                  }
                                  inputProps={{ min: 1, max: sheet.totalRows }}
                                  size="small"
                                  sx={{
                                    width: 120,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                    },
                                  }}
                                />
                                <Typography variant="body2" color="textSecondary">
                                  to
                                </Typography>
                                <TextField
                                  label="Last Row"
                                  type="number"
                                  value={sheet.lastRow}
                                  onChange={(e) =>
                                    handleRowRangeChange(
                                      sheet.name,
                                      "lastRow",
                                      parseInt(e.target.value, 10) || sheet.firstRow,
                                    )
                                  }
                                  inputProps={{
                                    min: sheet.firstRow,
                                    max: sheet.totalRows,
                                  }}
                                  size="small"
                                  sx={{
                                    width: 120,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1,
                                    },
                                  }}
                                />
                                <Box
                                  sx={{
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1,
                                    backgroundColor: STATUS_COLORS.info.bg,
                                    border: `1px solid ${STATUS_COLORS.info.border}`,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: STATUS_COLORS.info.text,
                                    }}
                                  >
                                    Range: {sheet.firstRow} - {sheet.lastRow}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
                <Box sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="gradient"
                      onClick={handleGenerateFlows}
                      disabled={
                        isProcessing || selectedSheetsCount === 0 || totalRowsToProcess === 0
                      }
                      startIcon={<GenerateIcon />}
                      sx={{
                        "&:disabled": {
                          background: "grey.300",
                          color: "grey.500",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Generate {totalRowsToProcess} tests
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Progress Display */}
          {importProgress && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                height: "100%",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
              >
                Generating Flows
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                {importProgress.current}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(importProgress.completed / importProgress.total) * 100}
                sx={{
                  mb: 1,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_TERTIARY} 100%)`,
                  },
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {importProgress.completed} of {importProgress.total} flows generated
              </Typography>
            </Paper>
          )}

          {/* Error Display */}
          {error && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: "error.main",
                },
              }}
            >
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
